import { Component, Inject, Injector, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OrdersService } from 'dm-src/app/modules/orders/orders.service';
import {
    ColorClass,
    FlashMessageService,
    PaymentType,
} from 'projects/shared/src/public-api';
import { DeliveryTaskType, OrderDetails } from 'shared';
import { DeliveryTaskState } from 'shared';
import { AUTH_SERVICE_IMPL, IAuthenticationService } from 'shared';
import { UserRole } from 'shared';

@Component({
    selector: 'app-order-data',
    templateUrl: './order-data.component.html',
    styleUrls: ['./order-data.component.scss'],
})
export class OrderDataComponent implements OnInit {
    public isEditMode = false;
    public orderDetails = this._ordersService.orderDetails;
    public hasCollectingTask = false;
    public hasDeliveringTask = false;
    public hasContainerPlacingTask = false;
    public collectingTaskCreationInProgress = false;
    public deliveringTaskCreationInProgress = false;
    public containerPlacingTaskCreationInProgress = false;
    public canCreateDeliveryTask = false;
    public DeliveryTaskType = DeliveryTaskType;
    public DeliveryTaskState = DeliveryTaskState;
    public PaymentType = PaymentType;

    public isPaymentTypeEditing = false;
    public selectedPaymentType: PaymentType;
    public isSaving = false;

    public isAdministrator = false;
    public isResettingMultipicking = false;
    public canResendConfirmationEmail = false;
    public isResendingConfirmationEmail = false;

    public get areDeliveryTaskButtonsVisible(): boolean {
        return (
            this.canCreateDeliveryTask &&
            (!this.hasCollectingTask || !this.hasDeliveringTask)
        );
    }

    private editablePaymentTypes: PaymentType[] = [
        PaymentType.PayOnDeliveryCard,
        PaymentType.PayOnDeliveryCash,
    ];

    public get isPaymentTypeEditVisible(): boolean {
        return this.editablePaymentTypes.includes(this.selectedPaymentType);
    }

    constructor(
        private _ordersService: OrdersService,
        private _flashMessageService: FlashMessageService,
        private _translateService: TranslateService,
        @Inject(AUTH_SERVICE_IMPL) private _authService: IAuthenticationService,
        private _serviceProvider: Injector
    ) {}

    ngOnInit(): void {
        this.orderDetails.subscribe((details) => {
            this.canCreateDeliveryTask = this._authService.currentUser.hasRoles([
                UserRole.Administrator,
            ]);
            this.isAdministrator = this._authService.currentUser.hasRoles([
                UserRole.Administrator,
            ]);
            this.canResendConfirmationEmail = this._authService.currentUser.hasRoles([
                UserRole.Administrator,
                UserRole.Supporter,
            ]);
            this.hasCollectingTask = details.deliveryTasks.some(
                (x) => x.taskType === DeliveryTaskType.Collecting
            );
            this.hasDeliveringTask = details.deliveryTasks.some(
                (x) => x.taskType === DeliveryTaskType.Delivering
            );
            this.hasContainerPlacingTask = details.deliveryTasks.some(
                (x) => x.taskType === DeliveryTaskType.ContainerPlacing
            );

            this.selectedPaymentType = details.paymentType;
        });
    }

    public createCollectingTask(): void {
        this.collectingTaskCreationInProgress = true;
        this._ordersService.createCollectingTask().subscribe(() => {
            this.collectingTaskCreationInProgress = false;
        });
    }

    public createContainerPlacingTask(): void {
        this.containerPlacingTaskCreationInProgress = true;
        this._ordersService.createContainerPlacingTask().subscribe(() => {
            this.containerPlacingTaskCreationInProgress = false;
        });
    }

    public createDeliveringTask(): void {
        this.deliveringTaskCreationInProgress = true;
        this._ordersService.createDeliveringTask().subscribe(() => {
            this.deliveringTaskCreationInProgress = false;
        });
    }

    public resetMultipickingIdentifier(): void {
        if (
            !confirm(
                this._translateService.instant(
                    'orders.reset-multipicking-identifier-confirm'
                )
            )
        ) {
            return;
        }

        this.isResettingMultipicking = true;

        this._ordersService.resetMultipickingIdentifier().subscribe({
            next: () => {
                this.isResettingMultipicking = false;
            },
            error: () => {
                this.isResettingMultipicking = false;

                alert(
                    this._translateService.instant(
                        'orders.reset-multipicking-identifier-failed'
                    )
                );
            },
        });
    }

    public resendOrderConfirmationEmail(): void {
        if (
            !confirm(
                this._translateService.instant(
                    'orders.resend-confirmation-email-confirm'
                )
            )
        ) {
            return;
        }

        this.isResendingConfirmationEmail = true;

        this._ordersService.resendOrderConfirmationEmail().subscribe({
            next: () => {
                this.isResendingConfirmationEmail = false;

                alert(
                    this._translateService.instant(
                        'orders.resend-confirmation-email-success'
                    )
                );
            },
            error: () => {
                this.isResendingConfirmationEmail = false;

                alert(
                    this._translateService.instant(
                        'orders.resend-confirmation-email-failed'
                    )
                );
            },
        });
    }

    public getPaymentTypeKey(paymentType: PaymentType): string {
        return `payment-type.${PaymentType[paymentType].toLowerCase()}`;
    }

    public cancelPaymentModeEdit(details: OrderDetails): void {
        this.selectedPaymentType = details.paymentType;
        this.isPaymentTypeEditing = false;
    }

    public savePaymentMode(details: OrderDetails): void {
        this.isSaving = true;

        this._ordersService
            .setPaymentType(PaymentType[this.selectedPaymentType])
            .subscribe({
                next: () => {
                    details.paymentType = this.selectedPaymentType;

                    this.isSaving = false;
                    this.isPaymentTypeEditing = false;
                },
                error: () => {
                    this.selectedPaymentType = details.paymentType;

                    this.isSaving = false;
                    this.isPaymentTypeEditing = false;

                    // TODO: this._flashMessageService.showMessage
                    alert('Save failed!');
                },
            });
    }
}
